// VERSION 3
import { Add, Clear } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import { Reference, useFetchAllReferencesLazyQuery } from '../../../../generated'
import { CustomController } from '../../common/CustomController'

const References = () => {
  const { control, getValues, watch } = useFormContext()

  const { fields, append, remove } = useFieldArray({
    name: 'references',
    control: control
  })

  useEffect(() => {
    if (getValues('references')?.length === 0) {
      append({ referenceId: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRemoveReference = (index: number) => {
    remove(index)
  }

  return (
    <>
      <Box my={2}>
        {fields.map((field, index) => (
          <Box key={field.id} my={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="body1" paddingBottom={1} fontWeight={500}>
                Referral #{index + 1}
              </Typography>
              {index !== 0 && (
                <IconButton onClick={() => handleRemoveReference(index)}>
                  <Clear color="primary" />
                </IconButton>
              )}
            </Box>

            <Box flex={2}>
              <Box display="flex" alignItems="center" gap={3}>
                <FormControl variant="outlined" sx={{ marginTop: 1.5, marginBottom: 1 }} fullWidth>
                  <Controller
                    name={`references.${index}.referenceId`}
                    control={control}
                    render={({ field }) => <ReferenceField {...field} index={index} />}
                  />
                </FormControl>

                <CustomController
                  controllerName={`references.${index}.percentage`}
                  controllerLabel="%"
                  fieldType="number"
                  fullWidth={false}
                  isDisabled={!watch(`references.${index}.referenceId`)}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      position: 'absolute',
                      bottom: '-1.5rem',
                      left: 0,
                      margin: 0,
                      width: '150px'
                    },
                    '& .MuiFormControl-root': {
                      position: 'relative'
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}

        <Button
          onClick={() => {
            append({ referenceId: '', percentage: 0 })
          }}
          variant="outlined"
          sx={{ display: 'flex', gap: 1, marginLeft: 'auto', marginTop: 4 }}
        >
          <Add />
          Add Referral
        </Button>
      </Box>
    </>
  )
}

export default References

export const ReferenceField = ({ index }: { index: number }) => {
  const [fetchAllReferences] = useFetchAllReferencesLazyQuery()
  const {
    watch,
    setValue,
    trigger,
    formState: { errors }
  } = useFormContext()
  const selectedReference = watch(`references.${index}.referenceId`) ?? undefined

  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [allReferences, setAllReferences] = useState<Reference[]>([])
  const [loading, setLoading] = useState(false)
  // The currently-selected reference object, fetched by id so it is always
  // available for display/pre-selection even when it is inactive or not part of
  // the current keyword/page of search results.
  const [selectedOption, setSelectedOption] = useState<Reference | null>(null)

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  // Search results: filtered by keyword + active only. The selected reference is
  // intentionally NOT pinned here — passing referenceId restricts the backend to
  // that single row, which is what previously broke search.
  useEffect(() => {
    const fetch = async () => {
      setLoading(true)

      const res = await fetchAllReferences({
        variables: {
          fetchAllReferences: {
            keyword: debouncedSearchTerm,
            active: true,
            paginationOptions: {
              limit: 50,
              page: currentPage
            }
          }
        }
      })

      const newRefs = (res.data?.fetchAllReferences?.references || []).filter(
        (ref: any): ref is Reference => ref !== null
      )

      setAllReferences(prev => (currentPage > 1 ? [...prev, ...newRefs] : newRefs))
      setLoading(false)
    }

    fetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, currentPage])

  // Resolve the selected reference to its full object (by id, without the active
  // filter) so an already-selected — possibly inactive — referral pre-selects
  // and stays visible in edit mode.
  useEffect(() => {
    const loadSelected = async () => {
      if (!selectedReference) {
        setSelectedOption(null)
        return
      }
      if (selectedOption?.id === selectedReference) return

      const res = await fetchAllReferences({
        variables: {
          fetchAllReferences: {
            referenceId: selectedReference,
            paginationOptions: { limit: 1, page: 1 }
          }
        }
      })

      const found =
        (res.data?.fetchAllReferences?.references || []).find((ref: any) => ref?.id === selectedReference) || null
      setSelectedOption(found as Reference | null)
    }

    loadSelected()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReference])

  // Always include the selected reference in the option list so its value resolves.
  const options = useMemo(() => {
    if (selectedOption && !allReferences.some(ref => ref.id === selectedOption.id)) {
      return [selectedOption, ...allReferences]
    }
    return allReferences
  }, [allReferences, selectedOption])

  const error = (errors.references as { [key: number]: any } | undefined)?.[index]?.referenceId
  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option: Reference) => option.referral || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => (
        <li {...props}>
          <span>{option.referral || ''} </span>
          <Typography component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
            ({option.referralSource?.sourceType || ''})
          </Typography>
        </li>
      )}
      value={selectedOption}
      loading={loading}
      onChange={(_, newValue) => {
        setSelectedOption(newValue)
        setValue(`references.${index}.referenceId`, newValue?.id)
        trigger(`references.${index}.referenceId`)
      }}
      onInputChange={(_, newInputValue, reason) => {
        // Only react to typed input; ignore the 'reset' that fires when an option is picked.
        if (reason === 'input') {
          setSearchTerm(newInputValue)
          setCurrentPage(1) // reset pagination when searching
        }
      }}
      ListboxProps={{
        onScroll: (event: React.UIEvent<HTMLUListElement>) => {
          const listboxNode = event.currentTarget
          const threshold = 40 // pixels from bottom to trigger

          if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - threshold) {
            setCurrentPage(prev => prev + 1)
          }
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={`Referral Source #${index + 1}`}
          error={!!error}
          helperText={error ? error.message : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          sx={{
            '& .MuiFormHelperText-root': {
              position: 'absolute',
              bottom: '-1.5rem',
              left: 0,
              margin: 0,
              width: '235px'
            },
            '& .MuiFormControl-root': {
              position: 'relative'
            }
          }}
        />
      )}
    />
  )
}
